import React, { useEffect, useState, useCallback, useContext } from 'react';

import { Row, Col, Alert } from 'antd';

// ROUTES
import { useHistory, useParams } from 'react-router-dom';
import {
	ROUTE_IIRGD_PESQUISA_ANTECEDENTE,
	// ROUTE_AAC_RESULTADO_NOMINAL,
} from 'pages/iirgd/Aac/routes/paths';

// REDUX
import { useSelector, useDispatch } from 'react-redux';
import { ApplicationState } from 'store';
import {
	enviaCentralLiberacaoClear,
	enviaCentralLiberacaoRequest,
} from 'store/modules/api/aac/novoPainelDeControle/enviaCentralLiberacao/actions';
import { limpaAtestadoAntecedentes } from 'store/modules/api/aac/emissaoAtestadoAntecedentes/actions';

// COMPONENTS
import Section from 'components/Common/Section';
import Button from 'components/Common/Button';
import ButtonVoltar from 'components/Common/ButtonVoltar';

// UTILS
import { formatDate, unformatDate } from 'utils/genericFunctions';
import { RequestEmissaoAtestadoAntecedentes } from 'store/modules/api/aac/emissaoAtestadoAntecedentes/types';
import Divider from 'components/Common/Divider';
import { EnviaCentralLiberacaoRequest } from 'store/modules/api/aac/novoPainelDeControle/enviaCentralLiberacao/types';
import { AuthContext, IAuthContext } from 'react-oauth2-code-pkce';
import { getCodigosMostrarEmTela, getMensagemAlerta } from './form';
import NavMenu from '../components/NavMenu';

interface DataFormEmissao {
	nome: string;
	numeroRg?: string;
	numeroDocumento?: string;
	sexo: string;
	nomePai: string;
	nomeMae: string;
	dataExpedicao: string;
	dataNascimento: string;
	mensagemRetorno?: string;
	codigoRetorno?: string;
}

interface Alerta {
	tipo: 'info' | 'error';
	mensagem: string;
	show: boolean;
}

const PesquisaAntecedentes: React.FC = () => {
	const { pesquisa }: { pesquisa: string } = useParams();

	const dispatch = useDispatch();
	const history = useHistory();

	const { token } = useContext<IAuthContext>(AuthContext);

	const {
		emissaoAtestadoAntecedentes,
		pesquisaCitaAAC,
		enviaCentralLiberacao,
	} = useSelector((state: ApplicationState) => state.api.aac);

	const [alerta, setAlerta] = useState<Alerta | null>(null);
	const [data, setData] = useState<RequestEmissaoAtestadoAntecedentes | null>(
		null,
	);

	// RECUPERA DATA
	useEffect(() => {
		if (emissaoAtestadoAntecedentes.pesquisa) {
			setData(emissaoAtestadoAntecedentes.pesquisa);
		}
	}, [emissaoAtestadoAntecedentes, pesquisaCitaAAC.data.numeroRg]);

	// RECUPERA ALERTA
	useEffect(() => {
		if (
			emissaoAtestadoAntecedentes?.data?.codigo &&
			getCodigosMostrarEmTela(emissaoAtestadoAntecedentes?.data?.codigo)
		) {
			setAlerta({
				tipo: 'info',
				mensagem: getMensagemAlerta(emissaoAtestadoAntecedentes?.data?.codigo),
				show: true,
			});
		}
		if (enviaCentralLiberacao.status === 200) {
			if (
				enviaCentralLiberacao.resultado &&
				enviaCentralLiberacao.resultado.codigoRetorno === 'C83' &&
				enviaCentralLiberacao.resultado.mensagemRetorno
			) {
				setAlerta({
					tipo: 'info',
					mensagem: enviaCentralLiberacao.resultado.mensagemRetorno || '',
					show: true,
				});
			} else {
				setAlerta({
					tipo: 'info',
					mensagem:
						'Encaminhado para Central de Liberação, acompanhe a análise no Painel de Controle.',
					show: true,
				});
			}
		}
	}, [
		emissaoAtestadoAntecedentes,
		enviaCentralLiberacao.resultado,
		enviaCentralLiberacao.status,
	]);

	const createPayload = (
		tipoRestricao: 'NUMERAL' | 'NOMINAL',
		tipoEncaminhamento: string,
	): EnviaCentralLiberacaoRequest => {
		const dataNew = emissaoAtestadoAntecedentes.pesquisa;
		const { dataExpedicaoRg } = dataNew;

		let dataNascimento = null;

		function convertDateFormat(date: any) {
			const parts = date.split('-');
			if (parts.length === 3) {
				return `${parts[2]}-${parts[1]}-${parts[0]}`;
			}
			return date;
		}

		if (dataNew.dataNascimento && dataNew.dataNascimento.includes('/')) {
			dataNascimento = unformatDate(dataNew.dataNascimento);
		} else if (dataNew.dataNascimento && dataNew.dataNascimento.includes('-')) {
			if (dataNew.dataNascimento.split('-')[0].length === 2) {
				dataNascimento = convertDateFormat(dataNew.dataNascimento);
			} else {
				dataNascimento = dataNew.dataNascimento;
			}
		}

		let numeroRg: string;
		if (tipoRestricao === 'NUMERAL') {
			// Check if numeroRg is a valid number
			if (Number(dataNew.numeroRg) && Number(dataNew.numeroRg) !== 0) {
				numeroRg = dataNew.numeroRg.toString();
			} else {
				const [numeroRgSemDigito] = pesquisaCitaAAC.data.numeroRg.split('-');
				numeroRg = numeroRgSemDigito; // Fallback to rg without digit
			}
		} else {
			// Assigning numeroRg for NOMINAL case
			numeroRg = dataNew.numeroDocumento.toString().replaceAll(' ', '');
		}

		let dc: string = '';
		if (
			tipoRestricao === 'NUMERAL' &&
			Number(dataNew.numeroRg) &&
			Number(dataNew.numeroRg) !== 0
		) {
			dc = dataNew.digitoRg.toString();
		}

		return {
			codigoRetorno: emissaoAtestadoAntecedentes.data?.codigo ?? '',
			nome: dataNew.nome,
			nomeSocial: dataNew.nomeSocial ?? '',
			nomeMae: dataNew.nomeMae,
			nomePai: dataNew.nomePai,
			sexo: dataNew.sexo,
			dataNascimento: dataNascimento || '',
			posto: dataNew.posto,
			ipEstacao: dataNew.ipEstacao,
			dataExpedicaoRg,
			tipoDocApresentado: dataNew.tipoDocApresentado,
			nomeProcurador: dataNew.nomeProcurador || '',
			status: '',
			docProcurador: dataNew.docProcurador || '',
			tipoEncaminhamento,
			usuario: dataNew.usuario,
			tipoRestricao,
			dc,
			cpf: dataNew.cpf ?? '',
			dcCpf: dataNew.dcCpf ?? '',
			numeroRg,
			origem: dataNew.origem ?? '',
		};
	};

	// ENVIO A CENTRAL
	const handleEnvioCentral = (tipoEncaminhamento: string) => {
		const tipoRestricao = pesquisa === 'numeral' ? 'NUMERAL' : 'NOMINAL';
		const payload = createPayload(tipoRestricao, tipoEncaminhamento);
		dispatch(enviaCentralLiberacaoRequest(token, payload));
	};

	const handleClickEncerrar = useCallback(() => {
		dispatch(limpaAtestadoAntecedentes());
		dispatch(enviaCentralLiberacaoClear());

		history.push(ROUTE_IIRGD_PESQUISA_ANTECEDENTE);
	}, [dispatch, history]);

	const handleButtonVoltar = useCallback(() => {
		if (pesquisa === 'numeral') {
			dispatch(limpaAtestadoAntecedentes());
			dispatch(enviaCentralLiberacaoClear());
			history.push(ROUTE_IIRGD_PESQUISA_ANTECEDENTE);
			return;
		}

		dispatch(enviaCentralLiberacaoClear());
		history.push(ROUTE_IIRGD_PESQUISA_ANTECEDENTE);
	}, [pesquisa, dispatch, history]);

	const [isDisabled, setIsDisabled] = useState<boolean>(false);

	useEffect(() => {
		setIsDisabled(
			!!(
				emissaoAtestadoAntecedentes.data.codigo === 'CO6' ||
				emissaoAtestadoAntecedentes.data.codigo === 'C13' ||
				enviaCentralLiberacao.status === 200
			),
		);
	}, [emissaoAtestadoAntecedentes.data.codigo, enviaCentralLiberacao.status]);

	const renderDoc = (dataProps: RequestEmissaoAtestadoAntecedentes) => {
		if (dataProps) {
			if (
				dataProps.tipoDocApresentado.toLocaleUpperCase() === 'CIN' &&
				dataProps.cpf &&
				dataProps.dcCpf
			) {
				return `${`${dataProps.cpf}-${dataProps.dcCpf}`}`;
			}
			if (dataProps.numeroRg && dataProps.digitoRg) {
				return dataProps.numeroRg.toString() + dataProps.digitoRg.toString();
			}
			if (dataProps.numeroDocumento) {
				return dataProps.numeroDocumento;
			}
		}
		return '';
	};

	const getDataGenre = (genre: string) => {
		switch (genre) {
			case 'M':
				return 'MASCULINO';
			case 'MASCULINO':
				return 'MASCULINO';
			case 'F':
				return 'FEMININO';
			case 'FEMININO':
				return 'FEMININO';
			default:
				return '';
		}
	};

	return (
		<>
			<Row>
				<Col span={24}>
					<NavMenu />

					<Divider mtSize={15} mbSize={15} />
				</Col>
			</Row>
			{alerta?.show && (
				<Alert
					style={{ marginBottom: '50px' }}
					message=""
					description={alerta.mensagem}
					type={alerta.tipo}
					closable
					showIcon
				/>
			)}

			<Section title="Resultados de Antecedentes">
				<Row gutter={[0, 20]}>
					<Col>
						<h3>Termos pesquisados</h3>
					</Col>
				</Row>

				<Row gutter={[0, 5]}>
					<Col span={24}>
						<Row gutter={[0, 0]}>
							<Col span={4}>
								<strong>Nome:</strong>
							</Col>

							<Col span={20}>{data?.nome}</Col>
						</Row>
					</Col>
					<Col span={12}>
						<Row gutter={[0, 5]}>
							<Col span={8}>
								<strong>RG/CIN:</strong>
							</Col>

							<Col span={12}>{data && renderDoc(data)}</Col>
						</Row>

						<Row gutter={[0, 5]}>
							<Col span={8}>
								<strong>Sexo:</strong>
							</Col>

							<Col span={12}>
								{(data?.sexo && getDataGenre(data.sexo)) || ''}
							</Col>
						</Row>

						<Row gutter={[0, 5]}>
							<Col span={8}>
								<strong>Filiação 1:</strong>
							</Col>

							<Col span={12}>{data?.nomePai}</Col>
						</Row>

						<Row>
							<Col span={8}>
								<strong>Filiação 2:</strong>
							</Col>

							<Col span={12}>{data?.nomeMae}</Col>
						</Row>
					</Col>

					<Col span={12}>
						<Row gutter={[0, 5]}>
							<Col span={8}>
								<strong>Data de Expedição:</strong>
							</Col>

							<Col span={12}>{formatDate(data?.dataExpedicaoRg) ?? ''}</Col>
						</Row>

						<Row>
							<Col span={8}>
								<strong>Data de Nascimento:</strong>
							</Col>

							<Col span={12}>
								{data && data?.dataNascimento
									? formatDate(data.dataNascimento)
									: ''}
							</Col>
						</Row>
					</Col>
				</Row>

				<Row gutter={[0, 50]}>
					<Col span={18} offset={4}>
						<Row gutter={[0, 10]}>
							<Col>
								<h4>
									Não foi possível emitir o Atestado de Antecedentes para o
									requerimento acima.
								</h4>
							</Col>
						</Row>

						{emissaoAtestadoAntecedentes?.data?.codigo &&
							getCodigosMostrarEmTela(
								emissaoAtestadoAntecedentes?.data?.codigo,
							) &&
							emissaoAtestadoAntecedentes?.data?.mensagem !== '' && (
								<Row>
									<Col>
										<strong>Motivo: </strong>
										{emissaoAtestadoAntecedentes?.data?.mensagem}
									</Col>
								</Row>
							)}
					</Col>
				</Row>

				<Row>
					<Col span={24}>
						<Row justify="space-between">
							<Col>
								<Row gutter={[0, 10]}>
									<Col>
										<Button
											disabled={isDisabled}
											style={{ width: '385px', height: '50px' }}
											onClick={() => handleEnvioCentral('E')}
										>
											Encaminhar para a Central de Liberação de AAC com E-mail
										</Button>
									</Col>
								</Row>

								<Row justify="center">
									<Col>
										<strong style={{ color: 'red' }}>(Certidão)</strong>
									</Col>
								</Row>
							</Col>

							<Col>
								<Button
									disabled={isDisabled}
									style={{ width: '360px', height: '50px' }}
									onClick={() => handleEnvioCentral('S')}
								>
									Encaminhar para a Central de Liberação de AAC e solicitar ao
									cidadão que aguarde o retorno da análise
								</Button>
							</Col>
						</Row>
					</Col>
				</Row>
			</Section>

			<Row justify="space-between">
				<Col>
					<ButtonVoltar navigate={false} onClick={handleButtonVoltar} />
				</Col>

				<Col style={{ marginTop: '13px' }}>
					<Button onClick={handleClickEncerrar}>Encerrar</Button>
				</Col>
			</Row>
		</>
	);
};

export default PesquisaAntecedentes;
